import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useLLMProxyServiceContext } from 'contexts/llmProxySerivceContext';
import type { PageLines } from 'services/imports/extractWithAssistant';
import { filesToPages } from 'services/imports/fileToPages';
import type { ImportPage } from 'services/imports/fileToPages';
import { MAX_SELECTED_PAGES } from 'services/imports/limits';
import { parseMarkdown } from 'services/imports/markdownContract';
import type { ImportTarget, LineSource, MarkdownLine } from 'services/imports/markdownContract';
import useAlert from 'utils/alert';
import { runExtraction } from 'views/imports/run-extraction';
import type { ImportPhase, OcrPageState, PageIssue, RunProgress } from 'views/imports/types';
import { withUniqueFileNames } from 'views/imports/unique-file-names';
import { isSelectablePage, usePageSelection } from 'views/imports/use-page-selection';
import type { AmendmentSuggestion } from 'services/imports/reviewAmendments';
import { runReview } from 'views/imports/run-review';
import { buildClosingRecords } from 'services/imports/closingRecords';
import {
  EMPTY_DOC, applyAmendmentToDoc, forgetRemoved, mergeExtraction, removeLine, removeMalformedLine, resetLine, restoreLine, setLineValue, setValueByKey, withLineIds,
} from 'views/imports/doc-model';
import type { ImportsDoc } from 'views/imports/doc-model';
import { extractedFactsByPage, findAmendmentTarget, markUnverified, setTargetInMarkdown, toAmendmentRows } from 'views/imports/utils';

export type Selection = { id: string | null; origin: 'list' | 'source' | null };
export type AmendmentRow = AmendmentSuggestion & { current: string | null };

/** Money values that need confirmation: plain amounts and charge amounts (charge.<letter>.<number>.<money field>). */
const MONEY_KEY_RE = /^(amount\.|charge\.[A-Z]\.[0-9]+\.(amount|buyer_at|buyer_before|seller_at|seller_before|by_others)$)/;
const isMoneyKey = (key: string): boolean => MONEY_KEY_RE.test(key);

export const useImportsState = () => {
  const { t } = useTranslation();
  const showAlert = useAlert();
  const { isServiceReady, llmServiceRef } = useLLMProxyServiceContext();

  const [pages, setPages] = useState<ImportPage[]>([]);
  const [ocrByPage, setOcrByPage] = useState<Map<string, OcrPageState>>(() => new Map());
  const [extractedPageIds, setExtractedPageIds] = useState<Set<string>>(() => new Set());
  const [doc, setDoc] = useState<ImportsDoc>(EMPTY_DOC);
  const [selection, setSelection] = useState<Selection>({ id: null, origin: null });
  const [unusedSource, setUnusedSource] = useState(false);
  const [previewRequest, setPreviewRequest] = useState<string | null>(null);
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [issues, setIssues] = useState<PageIssue[]>([]);
  const [cancelled, setCancelled] = useState(false);
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [scanTables, setScanTables] = useState(false);
  const [amendments, setAmendments] = useState<AmendmentSuggestion[]>([]);
  const [reviewFailedIds, setReviewFailedIds] = useState<Set<string>>(() => new Set());
  const [pendingAmount, setPendingAmount] = useState<AmendmentSuggestion | null>(null);
  const [cancelRequested, setCancelRequested] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);
  // Async steps (extraction merge, review) read the latest document, not the one from when they started.
  const docRef = useRef(doc);
  docRef.current = doc;
  const { markdown } = doc;

  /** Every edit keeps row ids and originals in step with the markdown. */
  const updateDoc = useCallback((change: (current: ImportsDoc) => ImportsDoc) => {
    setDoc((current) => withLineIds(change(current)));
  }, []);
  const {
    selectedPageIds, selectedCount, togglePageSelected, clearSelection, releaseExtracted,
  } = usePageSelection(pages);

  useEffect(() => () => {
    runIdRef.current = -1;
    abortRef.current?.abort();
  }, []);

  const linesByPage = useMemo<PageLines>(() => new Map(
    Array.from(ocrByPage.entries())
      .filter(([, state]) => state.status === 'done')
      .map(([pageId, state]) => [pageId, state.lines])
  ), [ocrByPage]);

  // Cited lines that do not exist on their page are shown as unverified (the markdown cannot carry that status).
  const parsed = useMemo(() => {
    const base = parseMarkdown(markdown);
    return { ...base, lines: markUnverified(base.lines, pages, linesByPage) };
  }, [markdown, pages, linesByPage]);
  const amendmentRows = useMemo(() => toAmendmentRows(amendments, parsed.lines), [amendments, parsed]);

  // Suggestions for a line sit on that line's row; the rest are listed on their own.
  const { inlineAmendments, unmatchedAmendments } = useMemo(() => {
    const inline: Record<string, AmendmentRow[]> = {};
    const unmatched: AmendmentRow[] = [];
    amendmentRows.forEach((row) => {
      const target = findAmendmentTarget(parsed.lines, row);
      if (target?.id) {
        inline[target.id] = [...(inline[target.id] ?? []), row];
      } else {
        unmatched.push(row);
      }
    });
    return { inlineAmendments: inline, unmatchedAmendments: unmatched };
  }, [amendmentRows, parsed]);

  const selectedLine: MarkdownLine | null = useMemo(
    () => parsed.lines.find((line) => line.id !== null && line.id === selection.id) ?? null,
    [parsed, selection.id]
  );
  const selectedSource = selectedLine?.src ?? null;
  const selectedLineHasNoSource = selectedLine !== null && selectedLine.src === null;
  const linkedSourceKeys = useMemo(() => new Set(parsed.lines
    .filter((line) => line.src !== null)
    .map((line) => `${line.src?.file}|${line.src?.page}|${line.src?.line}`)), [parsed]);

  const previewPageId = useMemo(() => {
    const viewable = pages.filter((page) => !page.failed);
    return (viewable.find((page) => page.id === previewRequest) ?? viewable[0])?.id ?? null;
  }, [pages, previewRequest]);

  const serviceReady = isServiceReady && llmServiceRef.current !== null;
  const hasPages = pages.length > 0;
  const hasResults = extractedPageIds.size > 0 || markdown.trim() !== '';
  const canExtract = serviceReady && phase === 'idle' && selectedCount > 0;
  const needsSelection = selectedCount === 0 && pages.some(isSelectablePage);

  const addFiles = useCallback(async (files: File[]) => {
    const myRunId = runIdRef.current;
    setPhase('converting');
    try {
      const newPages = await filesToPages(withUniqueFileNames(files, pages.map((page) => page.fileName)));
      if (runIdRef.current !== myRunId) return;
      setPages((prev) => [...prev, ...newPages]);
      const firstViewable = newPages.find((page) => !page.failed);
      if (firstViewable) setPreviewRequest((prev) => prev ?? firstViewable.id);
      if (newPages.some((page) => page.failed)) showAlert(t('imports:dropzone.prepareFailed'), 'error');
    } catch (error) {
      if (runIdRef.current === myRunId) showAlert(t('imports:errors.generic'), 'error');
    } finally {
      if (runIdRef.current === myRunId) setPhase('idle');
    }
  }, [pages, showAlert, t]);

  const rejectFiles = useCallback((rejections: FileRejection[]) => {
    const tooLarge = rejections.some((rejection) => rejection.errors.some((error) => error.code === 'file-too-large'));
    showAlert(t(tooLarge ? 'imports:dropzone.fileTooLarge' : 'imports:dropzone.unsupported'), 'error');
  }, [showAlert, t]);

  const noFileReceived = useCallback(() => {
    showAlert(t('imports:dropzone.noFileReceived'), 'error');
  }, [showAlert, t]);

  const runReviewFor = useCallback(async (
    targetPages: ImportPage[],
    ocrSnapshot: Map<string, OcrPageState>,
    markdownText: string,
    controller: AbortController,
    myRunId: number,
  ) => {
    const service = llmServiceRef.current;
    if (service === null) throw new Error('LLM service is not available');
    if (runIdRef.current !== myRunId) return;
    setPhase('reviewing');
    setProgress(null);
    const result = await runReview({
      pages: targetPages,
      ocrByPage: ocrSnapshot,
      extractedByPage: extractedFactsByPage(markdownText, targetPages),
      reviewImage: (prompt, imageDataUrl) => service.extractFromImage(prompt, imageDataUrl, controller.signal),
      signal: controller.signal,
      onProgress: (nextProgress) => {
        if (runIdRef.current !== myRunId) return;
        setProgress(nextProgress);
      },
    });
    if (runIdRef.current !== myRunId) return;
    const reviewedPageIds = new Set(targetPages.map((page) => page.id));
    setAmendments((prev) => [...prev.filter((amendment) => !reviewedPageIds.has(amendment.pageId)), ...result.amendments]);
    setReviewFailedIds((prev) => {
      const next = new Set(prev);
      result.reviewedPageIds.forEach((id) => next.delete(id));
      result.failedPageIds.forEach((id) => next.add(id));
      return next;
    });
    setIssues((prev) => [...prev.filter((issue) => issue.kind !== 'review-error'), ...result.issues]);
    if (controller.signal.aborted) setCancelled(true);
    if (result.issues.length > 0) showAlert(t('imports:issues.reviewError'), 'error');
  }, [llmServiceRef, showAlert, t]);

  const extract = useCallback(async () => {
    if (phase !== 'idle' || llmServiceRef.current === null) return;
    const myRunId = runIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setCancelled(false);
    setCancelRequested(false);
    setIssues([]);
    setProgress(null);
    setPhase('ocr');
    try {
      const selectedPages = pages.filter((page) => !page.failed && selectedPageIds.has(page.id));
      const result = await runExtraction({
        pages: selectedPages,
        ocrByPage,
        extractedPageIds,
        target: parsed.target,
        extractText: async (prompt) => {
          const service = llmServiceRef.current;
          if (service === null) throw new Error('LLM service is not available');
          return service.extractText(prompt, controller.signal);
        },
        scanTables,
        signal: controller.signal,
        onProgress: (nextPhase, nextProgress) => {
          if (runIdRef.current !== myRunId) return;
          setPhase(nextPhase);
          setProgress(nextProgress);
        },
      });
      if (runIdRef.current !== myRunId) return;
      setOcrByPage(result.ocrByPage);
      setExtractedPageIds(result.extractedPageIds);
      releaseExtracted(result.extractedPageIds);
      setIssues(result.issues);
      setCancelled(result.cancelled);
      const { markdown: extracted } = result;
      if (extracted !== null) {
        const merged = mergeExtraction(docRef.current, extracted);
        setDoc(merged.doc);
        if (merged.skipped.length > 0) {
          showAlert(t('imports:extract.keptEdits', { count: merged.skipped.length }), 'warning');
        }
      }
      if (result.issues.some((issue) => issue.kind === 'ocr-error' || issue.kind === 'extract-error')) {
        showAlert(t('imports:extract.partialFailure'), 'error');
      }
      if (scanTables && !result.cancelled) {
        const table = buildClosingRecords(selectedPages.map((page) => ({
          page,
          tables: result.ocrByPage.get(page.id)?.tables ?? [],
        })));
        setIssues((prev) => [...prev, ...table.issues.map((issue) => ({ pageId: issue.pageId, kind: 'table-check' as const, detail: issue.detail }))]);
      }
      if (reviewEnabled && !result.cancelled) {
        await runReviewFor(selectedPages, result.ocrByPage, result.markdown ?? '', controller, myRunId);
      }
    } catch (error) {
      if (runIdRef.current === myRunId) showAlert(t('imports:errors.extractFailed'), 'error');
    } finally {
      if (runIdRef.current === myRunId) {
        setPhase('idle');
        setProgress(null);
        setCancelRequested(false);
        abortRef.current = null;
      }
    }
  }, [phase, llmServiceRef, pages, selectedPageIds, ocrByPage, extractedPageIds, parsed.target, reviewEnabled, scanTables, runReviewFor, releaseExtracted, showAlert, t]);

  const retryReview = useCallback(async () => {
    if (phase !== 'idle' || llmServiceRef.current === null) return;
    const myRunId = runIdRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    setCancelled(false);
    setCancelRequested(false);
    try {
      await runReviewFor(
        pages.filter((page) => reviewFailedIds.has(page.id)),
        ocrByPage,
        docRef.current.markdown,
        controller,
        myRunId,
      );
    } catch (error) {
      if (runIdRef.current === myRunId) showAlert(t('imports:errors.generic'), 'error');
    } finally {
      if (runIdRef.current === myRunId) {
        setPhase('idle');
        setProgress(null);
        setCancelRequested(false);
        abortRef.current = null;
      }
    }
  }, [phase, llmServiceRef, pages, reviewFailedIds, ocrByPage, runReviewFor, showAlert, t]);

  // Cancel only acts on a live run; repeated clicks are ignored.
  const cancel = useCallback(() => {
    const controller = abortRef.current;
    if (controller === null || controller.signal.aborted) return;
    setCancelRequested(true);
    controller.abort();
  }, []);

  const clear = useCallback(() => {
    runIdRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setPages([]);
    setOcrByPage(new Map());
    setExtractedPageIds(new Set());
    setDoc(EMPTY_DOC);
    setSelection({ id: null, origin: null });
    setUnusedSource(false);
    setPreviewRequest(null);
    clearSelection();
    setPhase('idle');
    setProgress(null);
    setIssues([]);
    setCancelled(false);
    setAmendments([]);
    setReviewFailedIds(new Set());
    setPendingAmount(null);
    setCancelRequested(false);
  }, [clearSelection]);

  /** A box on the page selects the data line that cites it; a box no line cites is reported, not ignored. */
  const selectSource = useCallback((src: LineSource): string | null => {
    const match = parsed.lines.find((line) => (
      line.id !== null && line.src !== null && line.src.file === src.file && line.src.page === src.page && line.src.line === src.line
    ));
    if (match?.id) {
      setSelection({ id: match.id, origin: 'source' });
      setUnusedSource(false);
      return match.key;
    }
    setSelection({ id: null, origin: null });
    setUnusedSource(true);
    return null;
  }, [parsed]);

  const selectLine = useCallback((id: string | null) => {
    setSelection({ id, origin: id === null ? null : 'list' });
    setUnusedSource(false);
  }, []);

  const changeTarget = useCallback((target: ImportTarget | null) => {
    updateDoc((current) => ({ ...current, markdown: setTargetInMarkdown(current.markdown, target) }));
  }, [updateDoc]);

  const setDataValue = useCallback((key: string, value: string) => {
    updateDoc((current) => setValueByKey(current, key, value));
  }, [updateDoc]);

  const setRowValue = useCallback((id: string, value: string) => {
    updateDoc((current) => setLineValue(current, id, value));
  }, [updateDoc]);

  const resetRow = useCallback((id: string) => {
    updateDoc((current) => resetLine(current, id));
  }, [updateDoc]);

  const removeRow = useCallback((line: MarkdownLine) => {
    updateDoc((current) => (line.id === null ? removeMalformedLine(current, line.lineNo) : removeLine(current, line.id)));
  }, [updateDoc]);

  const restoreRow = useCallback((id: string) => {
    updateDoc((current) => restoreLine(current, id));
  }, [updateDoc]);

  /** Removed rows stay restorable until the markdown is sent to the assistant. */
  const markSent = useCallback(() => {
    updateDoc(forgetRemoved);
  }, [updateDoc]);

  const applyAcceptedAmendment = useCallback((amendment: AmendmentSuggestion) => {
    updateDoc((current) => applyAmendmentToDoc(current, amendment));
    setAmendments((prev) => prev.filter((candidate) => candidate.id !== amendment.id));
  }, [updateDoc]);

  const acceptAmendment = useCallback((id: string) => {
    const amendment = amendments.find((candidate) => candidate.id === id);
    if (!amendment) return;
    if (isMoneyKey(amendment.key)) {
      setPendingAmount(amendment);
      return;
    }
    applyAcceptedAmendment(amendment);
  }, [amendments, applyAcceptedAmendment]);

  const confirmPendingAmount = useCallback(() => {
    if (pendingAmount === null) return;
    applyAcceptedAmendment(pendingAmount);
    setPendingAmount(null);
  }, [pendingAmount, applyAcceptedAmendment]);

  const cancelPendingAmount = useCallback(() => setPendingAmount(null), []);

  const pendingAmountCurrent = useMemo(() => {
    if (pendingAmount === null) return null;
    const target = findAmendmentTarget(parsed.lines, pendingAmount);
    return target ? target.value : null;
  }, [pendingAmount, parsed]);

  const dismissAmendment = useCallback((id: string) => {
    setAmendments((prev) => prev.filter((candidate) => candidate.id !== id));
  }, []);

  return {
    pages,
    ocrByPage,
    extractedPageIds,
    markdown,
    removedRows: Object.entries(doc.removed).map(([id, snapshot]) => ({ id, ...snapshot })),
    originals: doc.originals,
    selectedId: selection.id,
    selectionOrigin: selection.origin,
    unusedSource,
    linkedSourceKeys,
    phase,
    progress,
    issues,
    cancelled,
    cancelling: cancelRequested,
    amendmentRows,
    inlineAmendments,
    unmatchedAmendments,
    reviewFailedCount: reviewFailedIds.size,
    retryReview,
    reviewEnabled,
    setReviewEnabled,
    scanTables,
    setScanTables,
    parsed,
    linesByPage,
    selectedSource,
    selectedLineHasNoSource,
    previewPageId,
    selectedPageIds,
    selectedCount,
    maxSelectedPages: MAX_SELECTED_PAGES,
    needsSelection,
    serviceReady,
    hasPages,
    hasResults,
    canExtract,
    addFiles,
    rejectFiles,
    noFileReceived,
    extract,
    cancel,
    clear,
    selectLine,
    setRowValue,
    resetRow,
    removeRow,
    restoreRow,
    markSent,
    selectPreviewPage: setPreviewRequest,
    togglePageSelected,
    clearSelection,
    selectSource,
    changeTarget,
    setDataValue,
    acceptAmendment,
    dismissAmendment,
    pendingAmount,
    pendingAmountCurrent,
    confirmPendingAmount,
    cancelPendingAmount,
  };
};
